import { useQuery } from "@apollo/client";
import { Button, Dropdown, LoadingSpinner, Pagination } from "pepsico-ds";
import { useMemo, useState } from "react";
import { listProfilesBySegment } from "../../../graphql/queries/listSegments";
import { exportWithPolling } from "../../../utils/fileTransfer";
import { FileHistoryButton } from "../../fileManager";
import SegmentUsersImportModal from "../segmentDialogModals/SegmentUsersImportModal";
import "./_platform-segments-users-list.scss";

export default function SegmentUsersList({ segmentId, showToast }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const { data, loading, refetch } = useQuery(listProfilesBySegment, {
    variables: { segmentId, page, size },
    skip: !segmentId,
    fetchPolicy: "cache-and-network",
  });

  const pageCount = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil((data?.listProfilesBySegment?.total || 0) / (size || 1))
      ),
    [data?.listProfilesBySegment?.total, size]
  );

  const handleRowsChange = (opt) => {
    const val = Number(opt?.[0]?.id || opt?.[0]);
    if (!Number.isNaN(val) && val > 0) {
      setSize(val);
    }
  };

  const handlePageChange = (p) => {
    const next = Number(p) || 1;
    setPage(next);
  };

  const handleExportSchedule = async () => {
    try {
      setIsLoading(true);
      showToast("info", "Export started. Your download should begin shortly.");
      const result = await exportWithPolling({
        context: "USERS_BY_SEGMENT",
        parameters: { segment_id: segmentId },
        exportType: "text/csv",
        filename: `segment-${segmentId}-users.csv`,
        polling: false,
      });
      if (result.status === 200) {
        showToast("success", result.message, result.description);
      }
    } catch (e) {
      console.error(e);
      showToast("error", `Failed to export schedule. ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="segment-users-list-card">
      <div className="font-xs mb-3 display-flex justify-content-space-between align-items-center">
        <strong>Users</strong>
        <div className="display-flex align-items-center gap-2">
          <Button
            size="medium"
            variant="secondary"
            iconLeading="article"
            onClick={() => setShowAddModal(true)}
          >
            Import Users
          </Button>
          <Button
            size="medium"
            variant="secondary"
            iconTrailing="download"
            onClick={handleExportSchedule}
            isLoading={isLoading}
            disabled={isLoading || data?.listProfilesBySegment?.items?.length === 0}
          >
            Export Users
          </Button>
          <FileHistoryButton
            contentType="text/csv"
            context="USERS_BY_SEGMENT"
            defaultTab="exports"
            variant="secondary"
          />
        </div>
      </div>
      <div className="segment-users-list-container">
        <div className="mb-2">
          <div className="table-scroll">
            <table className="segment-users-list-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th> Email</th>
                  <th> Phone</th>
                  <th>National Id</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="text-center font-bold">
                      <LoadingSpinner size={{ height: "24px", width: "24px" }} />
                    </td>
                  </tr>
                )}
                {!loading && !data?.listProfilesBySegment?.items?.length && (
                  <tr>
                    <td colSpan={6} className="text-center font-bold">
                      No users found
                    </td>
                  </tr>
                )}
                {data?.listProfilesBySegment?.items?.map((user) => (
                  <tr key={user.id}>
                    <td>{user.userId}</td>
                    <td>{user.userInfo?.firstName}</td>
                    <td>{user.userInfo?.lastName}</td>
                    <td>{user.userInfo?.emailAddress}</td>
                    <td>{user.userInfo?.phone}</td>
                    <td>{user.userInfo?.nationalId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-container display-flex justify-content-space-between mt-2">
            <Dropdown
              childList={[
                { id: 10, displayText: 10 },
                { id: 20, displayText: 20 },
                { id: 50, displayText: 50 },
                { id: 100, displayText: 100 },
              ]}
              label="Rows per page"
              selectedValue={[{ id: size, displayText: size }]}
              selection="single"
              setSelectedValue={handleRowsChange}
            />
            <Pagination
              currentPage={page}
              pageCount={pageCount}
              size="medium"
              variant="number"
              onUpdate={handlePageChange}
            />
          </div>
        </div>
      </div>
      <SegmentUsersImportModal
        open={showAddModal}
        handleClose={() => setShowAddModal(false)}
        segmentId={segmentId}
        refetchSegmentUsers={refetch}
        showToast={showToast}
      />
    </div>
  );
}
